/**
 * Detect a direct video FILE url ("https://cdn.agency.com/spot.mp4") so "Add by
 * URL" can attach the video itself instead of screenshotting whatever page the
 * link points at, and name the video hosts we cannot read at all.
 *
 * WHY the second half exists: a Vimeo / Drive / Dropbox share link used to
 * become a still image of a player page. The video read then found nothing, and
 * the questionnaire was authored blind with no error anywhere. Naming the host
 * turns that silent output corruption into one sentence the marketer can act on.
 *
 * PRECEDENCE: `unreadableVideoHost` wins over `isDirectVideoUrl`. A Dropbox
 * share link can end in `.mp4` and still serve an HTML page, so fetching it
 * would hand Gemini a web page labelled as video.
 *
 * STRICTNESS: the parser below is deliberately narrower than `new URL`. Every
 * ambiguous form is refused outright rather than guessed at, because the two
 * languages guess differently and a divergence here is a silent mislabel:
 * backslash separators, protocol-relative "//host/x", nested schemes
 * ("blob:https://..."), spaces in the host, and out-of-range ports. Hosts are
 * IDNA-folded first, so a fullwidth-dot "vimeo.com" is still Vimeo.
 *
 * Must stay in lockstep with `apps/python-api/survey/media/video_url.py`; both
 * halves are pinned to `video-url.cases.json`.
 */

import { isYoutubeUrl } from "./youtube-url";

const DIRECT_VIDEO_EXTENSIONS = [".mp4", ".webm", ".mov"];

const DIRECT_VIDEO_MIME_TYPES: Record<string, string> = {
  ".mp4": "video/mp4",
  ".webm": "video/webm",
  ".mov": "video/quicktime",
};

/**
 * Hosts whose "video" links are player PAGES, not video bytes. Matched on the
 * host itself and on any subdomain of it (player.vimeo.com, www.dropbox.com).
 */
const UNREADABLE_VIDEO_HOSTS: Array<{ host: string; label: string }> = [
  { host: "vimeo.com", label: "Vimeo" },
  { host: "drive.google.com", label: "Google Drive" },
  { host: "docs.google.com", label: "Google Drive" },
  { host: "dropbox.com", label: "Dropbox" },
];

/** A scheme prefix. The char after ":" tells a scheme from a "host:port". */
const SCHEME_PREFIX = /^([A-Za-z][A-Za-z0-9+.-]*):/;

/** Post-IDNA hosts are ASCII. Anything else (spaces, brackets) is refused. */
const HOST_PATTERN = /^[a-z0-9_-]+(\.[a-z0-9_-]+)*$/;

/**
 * Parse to an http(s) URL, defaulting a bare host to https, and refuse every
 * form the two languages would disagree about. Returns the normalized host and
 * the path, never a URL object: the callers only ever need those two.
 */
function parseHttpUrl(raw: string): { host: string; path: string } | null {
  const trimmed = (raw || "").trim();
  if (!trimmed) {
    return null;
  }
  // A backslash is a path separator to some parsers and a literal to others.
  if (trimmed.includes("\\")) {
    return null;
  }
  // No authority to speak of. A bare path ("/spot.mp4") and a protocol-relative
  // "//host/x" are both refused: WHATWG `new URL` folds the first path segment
  // into the HOST for these ("https:///s/abc/spot.mp4" parses with host "s"),
  // while urlparse leaves the host empty. Guessing either way is a mislabel.
  if (trimmed.startsWith("/")) {
    return null;
  }
  if (/^https?:\/\/\//i.test(trimmed)) {
    return null;
  }
  let candidate = trimmed;
  const scheme = SCHEME_PREFIX.exec(trimmed);
  if (scheme) {
    const rest = trimmed.slice(scheme[0].length);
    const isHttp = /^https?$/i.test(scheme[1]) && rest.startsWith("//");
    // A colon followed by digits is a port on a bare host ("cdn.example:8080"),
    // not a scheme. Anything else that is not http(s) is a foreign or nested
    // scheme (blob:, data:, ftp:, mailto:) and is refused.
    const isHostPort = /^[0-9]/.test(rest);
    if (!isHttp && !isHostPort) {
      return null;
    }
    if (!isHttp) {
      candidate = `https://${trimmed}`;
    }
  } else {
    candidate = `https://${trimmed}`;
  }
  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return null;
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    return null;
  }
  // `new URL` has already IDNA-folded the host (a fullwidth dot becomes ".").
  const host = parsed.hostname.toLowerCase();
  if (!HOST_PATTERN.test(host)) {
    return null;
  }
  if (parsed.port) {
    const port = Number(parsed.port);
    if (!Number.isInteger(port) || port < 1 || port > 65535) {
      return null;
    }
  }
  return { host, path: parsed.pathname };
}

function directVideoExtension(raw: string): string | null {
  if (unreadableVideoHost(raw) !== null) {
    return null;
  }
  const parsed = parseHttpUrl(raw);
  if (parsed === null) {
    return null;
  }
  const path = parsed.path.toLowerCase();
  return DIRECT_VIDEO_EXTENSIONS.find((ext) => path.endsWith(ext)) ?? null;
}

/** The display name of a video host we cannot read, else null. */
export function unreadableVideoHost(raw: string): string | null {
  const parsed = parseHttpUrl(raw);
  if (parsed === null) {
    return null;
  }
  for (const entry of UNREADABLE_VIDEO_HOSTS) {
    if (parsed.host === entry.host || parsed.host.endsWith(`.${entry.host}`)) {
      return entry.label;
    }
  }
  return null;
}

/** True when the URL points straight at video bytes we can fetch and read. */
export function isDirectVideoUrl(raw: string): boolean {
  return directVideoExtension(raw) !== null;
}

/** The mime type implied by a direct video URL's extension, else null. */
export function directVideoMimeType(raw: string): string | null {
  const ext = directVideoExtension(raw);
  return ext ? DIRECT_VIDEO_MIME_TYPES[ext] : null;
}

/**
 * One sentence telling the user what to do instead. Null when readable.
 *
 * Deliberately surface-agnostic: this string is shown in the twin chat room as
 * well as the questionnaire builder, so it names the host and points at the
 * upload.
 */
export function unreadableVideoHostMessage(raw: string): string | null {
  const label = unreadableVideoHost(raw);
  if (!label) {
    return null;
  }
  return `We can't read a ${label} link. Upload the video file instead.`;
}

/**
 * Study-stimulus refusal: twins need ffmpeg keyframes, which we cannot extract
 * from a YouTube watch URL (never downloaded) or from a player-page host.
 *
 * YouTube is NOT an `unreadableVideoHost` — Knowledge Hub and Quick Study still
 * ingest watch URLs via Gemini file_uri. This helper is the study-only gate.
 */
export const YOUTUBE_NON_KEYFRAMEABLE_MESSAGE =
  "We can't extract frames from a YouTube link. Upload the video file instead.";

export function nonKeyframeableVideoMessage(raw: string): string | null {
  if (isYoutubeUrl(raw)) {
    return YOUTUBE_NON_KEYFRAMEABLE_MESSAGE;
  }
  return unreadableVideoHostMessage(raw);
}
